import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  IconButton,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet-async';

const campaignImage = `https://${window.location.host}/images/share-image.png`;

const faqs = [
  {
    question: 'How does PurelyGive offer zero fees for nonprofits?',
    answer:
      'PurelyGive ensures 100% of every donation goes directly to nonprofits by covering all transaction fees through optional donor tips. This donor-supported model allows us to operate with no cost to the organizations we serve, maximizing the impact of each gift.',
    category: 'General',
  },
  {
    question: 'Is there a monthly fee to use PurelyGive?',
    answer:
      'No. There are no monthly fees, subscription costs, or hidden charges. Our platform is completely free for nonprofits to use.',
    category: 'Pricing',
  },
  {
    question: 'How do I withdraw my funds?',
    answer:
      'Once your organization\'s bank account is connected through our payment processor, approved donations are automatically deposited into your account. Withdrawals are processed daily on business days, based on when donations complete processing. No manual scheduling is required.',
    category: 'Withdrawals',
  },
  {
    question: 'Can donors see how much of their donation goes to fees?',
    answer:
      'Yes. We are committed to transparency. Donors see that 100% of their donation supports the nonprofit, with no deductions. They are also given the option to leave a tip to support PurelyGive’s fee-free fundraising model.',
    category: 'Donations',
  },
  {
    question: 'How do I get started with PurelyGive?',
    answer:
      'It only takes a few minutes to get started. Sign up for a free account, create your nonprofit profile, and start receiving donations. Our platform walks you through the setup process step-by-step.',
    category: 'General',
  },
  {
    question: 'What payment methods does PurelyGive support?',
    answer:
      'We currently accept ACH bank transfers and all major credit and debit cards. Google Pay and Apple Pay integration is coming soon, offering even more flexibility for donors.',
    category: 'Donations',
  },
  {
    question: 'Do I have to pay extra for customer support?',
    answer:
      'No. Our support team is available to help you at no additional cost. Whether you have technical questions or need help optimizing your campaign, we’re here for you.',
    category: 'Support',
  },
  {
    question: 'How secure is PurelyGive\'s platform?',
    answer:
      'We use bank-level encryption and comply with industry-standard security protocols to ensure your data and transactions are always protected.',
    category: 'Security',
  },
  {
    question: 'Does PurelyGive support recurring donations?',
    answer:
      'Yes. Donors can choose between one-time and recurring donations, enabling consistent support for your cause.',
    category: 'Donations',
  },
  {
    question: 'Can I customize my donation forms?',
    answer:
      'Yes. PurelyGive allows you to customize the appearance of your donation forms to reflect your organization\'s branding, ensuring a cohesive and professional donor experience.',
    category: 'Features',
  },
  {
    question: 'When do donations show up in my account?',
    answer:
      'Donations typically appear in your account within minutes of being submitted. Bank transfers may take 2–4 business days to settle, while card payments settle faster.',
    category: 'Withdrawals',
  },
  {
    question: 'Are donations through PurelyGive tax-deductible?',
    answer:
      'Yes. Donations are tax-deductible when made to a registered 501(c)(3) nonprofit. Donors receive a receipt they can use for tax reporting immediately after donating.',
    category: 'Donations',
  },
  {
    question: 'How do donors receive receipts?',
    answer:
      'Donors receive an automated email receipt immediately after their donation is processed. The receipt includes the donation amount, the nonprofit name, and any applicable tax information.',
    category: 'Donations',
  },
  {
    question: 'What happens if a donation fails or is refunded?',
    answer:
      'If a donation fails (e.g., due to a declined card or insufficient funds), the donor is notified immediately. If a donation is refunded, both the donation and any associated tip are returned to the donor’s original payment method.',
    category: 'Donations',
  },
  {
    question: 'Can I share my campaign or donation page?',
    answer:
      'Yes. Every organization and campaign page includes a unique shareable link that can be posted to social media, email newsletters, or your website to encourage more support.',
    category: 'Features',
  },
  {
    question: 'Is there a minimum or maximum donation amount?',
    answer:
      'The minimum donation amount is $1. There is no maximum limit, though donations over $10,000 may be subject to additional verification to prevent fraud.',
    category: 'Donations',
  },
  {
    question: 'Can donors give anonymously?',
    answer:
      'Yes. Donors have the option to give anonymously during checkout. Their name will not be shared publicly or with the organization unless they choose otherwise.',
    category: 'Donations',
  },
  {
    question: 'How can I update my nonprofit’s information?',
    answer:
      'Log into your PurelyGive dashboard at any time to update your nonprofit profile, manage banking details, edit campaigns, and more—all from one secure place.',
    category: 'General',
  },
];

function FAQ() {
  const [searchTerm, setSearchTerm] = useState('');
  const bgColor = useColorModeValue('#f5f3ee', 'gray.800');

  const filteredFaqs = faqs.filter((faq) =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={6} bg={bgColor} minHeight="100vh">
      <Helmet>
        <title>Frequently Asked Questions - PurelyGive</title>
        <meta
          name="description"
          content="Find answers to common questions about PurelyGive. Learn how our zero-fee fundraising platform works for nonprofits and donors."
        />
        <meta
          name="keywords"
          content="FAQ, questions, answers, support, PurelyGive, fundraising platform"
        />
        <meta property="og:title" content="PurelyGive FAQs" />
        <meta
          property="og:description"
          content="Get answers to frequently asked questions about PurelyGive's zero-fee fundraising platform. Learn more today."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/faq" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PurelyGive FAQs" />
        <meta
          name="twitter:description"
          content="Find answers about PurelyGive pricing, setup, donation processing, and how nonprofits keep more from every gift."
        />
        <meta name="twitter:image" content={campaignImage} />
      </Helmet>
      <VStack spacing={8} mx="auto" maxW="800px">
        <Box textAlign="center">
          <Heading as="h1" size="2xl" mb={4} color="#f08453">
            Frequently Asked Questions
          </Heading>
          <Text fontSize="lg" color="#0f393b">
            Have questions? We've got answers.
          </Text>
        </Box>

        {/* Search Bar */}
        <Flex w="100%" maxW="600px" mt={4} mb={6}>
          <Input
            placeholder="Search FAQs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRightRadius="0"
            bg="white"
          />
          <IconButton
            icon={<SearchIcon />}
            aria-label="Search FAQs"
            borderLeftRadius="0"
            colorScheme="orange"
          />
        </Flex>

        {/* Accordion */}
        <Accordion allowMultiple w="100%">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <AccordionItem key={index} border="none" mb={2}>
                <AccordionButton
                  _expanded={{ bg: '#f08453', color: 'white' }}
                  bg="white"
                  borderRadius="md"
                  shadow="sm"
                >
                  <Box flex="1" textAlign="left" fontWeight="bold" fontSize="lg">
                    {faq.question}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} bg="white" mt={2} borderRadius="md" shadow="sm">
                  {faq.answer}
                </AccordionPanel>
              </AccordionItem>
            ))
          ) : (
            <Text>No FAQs found matching your search.</Text>
          )}
        </Accordion>
      </VStack>
    </Box>
  );
}

export default FAQ;
